<template>
	<div class="canvas-calendar-wrapper">
		<div v-if="needPlanner" class="calendar-planner">
			<div v-if="eventMove" class="event-mover line-items">
				<sym-icon name="click" class="animated-click"></sym-icon>
				<b>{{ env.msg('event.plan.moveEvent') }}:</b>
				<ref-link :src-type="SrcType.EVENT" :src-id="getIntValue(today.createContext, ContextKey.EVENT)"></ref-link>
				<b>{{ env.msg('event.plan.original') }}:</b>
				<select v-model.number="calStore.originalEventAction">
					<option :value="ORIGINAL_KEEP">{{ env.msg('event.plan.keep') }}</option>
					<option :value="ORIGINAL_MOVE">{{ env.msg('event.plan.move') }}</option>
					<option :value="ORIGINAL_FULFILL">{{ env.msg('event.plan.fulfill') }}</option>
					<option :value="ORIGINAL_CANCEL">{{ env.msg('event.plan.cancel') }}</option>
				</select>
			</div>
			<div v-else class="event-context line-items">
				<sym-icon name="click" class="animated-click"></sym-icon>
				<b>{{ env.msg(createSrcType === SrcType.EVENT ? 'event.plan' : 'contact.plan') }}:</b>
				<template v-for="(p, idx) in refLinks">
					<ref-link :customer="p.customer" :branch="p.branch" :person="p.person" :src-type="p.srcType" :src-id="p.srcId"></ref-link>
				</template>
			</div>
			<close-cross @click.prevent="calStore.cancelPlanner()"></close-cross>
		</div>
		<div class="canvas-calendar-wrapper" :style="wrapperStyle" ref="wrapper">
			<template v-if="width>0 && height>0">
				<container-split v-if="isSplit" name="duocal" :ratio="50" store="local" ref="duoSplit">
					<template #primary><inner-calendar-canvas :secondary="false" :logical-width="primaryWidth" :logical-height="height" :name="name"
								  :display="display" :show-attendees="showAttendees" :min-time="minTime" :max-time="maxTime"
								  :snap-minutes="snapMinutes" :snap-monday="snapMonday" :show-time="showTime" :autocomplete="autocomplete"
								  :event-alarm="eventAlarm" :whole-day-event-alarm="wholeDayEventAlarm"></inner-calendar-canvas></template>
					<template #secondary><inner-calendar-canvas :secondary="true" :logical-width="secondaryWidth" :logical-height="height" :name="name+'-sec'"
								:display="display" :show-attendees="showAttendees" :min-time="minTime" :max-time="maxTime"
								:snap-minutes="snapMinutes" :snap-monday="snapMonday" :show-time="showTime" :autocomplete="autocomplete"
								:event-alarm="eventAlarm" :whole-day-event-alarm="wholeDayEventAlarm"></inner-calendar-canvas></template>
				</container-split>
				<inner-calendar-canvas v-else :secondary="false" :logical-width="width" :logical-height="height" :name="name"
									   :display="display" :show-attendees="showAttendees" :min-time="minTime" :max-time="maxTime"
									   :snap-minutes="snapMinutes" :snap-monday="snapMonday" :show-time="showTime" :autocomplete="autocomplete"
									   :event-alarm="eventAlarm" :whole-day-event-alarm="wholeDayEventAlarm"></inner-calendar-canvas>
			</template>
			<template v-if="splitPossible">
				<div class="splitters">
					<div class="horizontal-splitter" @pointerdown="resizeStart" ref="resizer">
						<span class="horizontal-grab"></span>
					</div>
					<icon-button v-if="splitPossible" :name="splitFlag ? 'close-right-panel':'open-right-panel'" class="clone-icon" @click="toggleDualSplit"></icon-button>
				</div>
			</template>
			<template v-else>
				<div class="horizontal-splitter" @pointerdown="resizeStart" ref="resizer">
					<span class="horizontal-grab"></span>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
/**
 * This wrapping component is responsible for managing inner canvas dimensions.
 */
import {ref, computed, type CSSProperties, onMounted, onUnmounted} from "vue";
import InnerCalendarCanvas from "@/event/calendar/InnerCalendarCanvas.vue";
import {AppContextMap, ContextKey, getIntValue, hasAnyValue, hasValue} from "@t/fd/AppContext.ts";
import {injectEnv} from "@fd/Environment.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";
import {SrcType} from "@t/meta/SourceType.ts";
import RefLink from "@fd/helpers/RefLink.vue";
import CloseCross from "@fd/editors/file/CloseCross.vue";
import {useTodayStore} from "@/store/TodayStore.ts";
import {storeToRefs} from "pinia";
import {useCalendarStore, ORIGINAL_CANCEL, ORIGINAL_FULFILL, ORIGINAL_MOVE, ORIGINAL_KEEP} from "@/store/CalendarStore.ts";
import IconButton from "@fd/helpers/IconButton.vue";
import ContainerSplit from "@/ui/splitter/ContainerSplit.vue";


const props = withDefaults(defineProps<{
	/**
	 * This name is used to save/remember last state of the calendar. If you don't provide it
	 * the state won't be saved.
	 */
	name?: string;
	/**
	 * Initial height in pixels. This is used only if there isn't previously saved height.
	 * Default is 75% of screen height.
	 */
	initialHeight?: number;

	/** Display string. Determines what will be displayed inside the event box */
	display?: string;
	/** Show attendees using monogram, full name or not at all*/
	showAttendees?: "no" | "mono" | "full";

	/** Determine working hours */
	minTime?: number;
	maxTime?: number;

	snapMinutes?: number;
	snapMonday?: boolean;
	showTime?: boolean;
	autocomplete?: boolean;
	eventAlarm?: number;
	wholeDayEventAlarm?: number;
	/** Support for splitting calendar into two panels. */
	canSplit?: boolean;
}>(), {
	name: "",
	initialHeight: 0,
	display: "SPX",		// subject ~ place ~ customer
	showAttendees: "mono",
	minTime: 8,
	maxTime: 18,
	snapMinutes: 5,
	snapMonday: false,
	showTime: true,
	autocomplete: false,
	eventAlarm: 300000,
	wholeDayEventAlarm: 86400000,
	canSplit: false,
});

// Canvas dimensions
const width = ref<number>(0);
const height = ref<number>(initialHeight());
const RESIZER_HEIGHT = 16;
const MIN_HEIGHT = 300;
const resizer = ref<HTMLElement | null>(null);

const primaryWidth = computed(() => {
	if (duoSplit.value) {
		return duoSplit.value.primaryWidth;
	}
	return Math.floor((width.value - 16)/2);
});
const secondaryWidth = computed(() => {
	if (duoSplit.value) {
		return duoSplit.value.secondaryWidth;
	}
	return Math.floor((width.value - 16)/2);
});

const env = injectEnv();

const today = useTodayStore();
const { createSrcType } = storeToRefs(today);
const calStore = useCalendarStore();

const needPlanner = computed<boolean>(() => {
	return hasAnyValue(today.createContext, ContextKey.EVENT, ContextKey.CUSTOMER, ContextKey.OPPORTUNITY, ContextKey.BUSCASE,
		ContextKey.PROJECT, ContextKey.PROBLEM, ContextKey.CAMPAIGN, ContextKey.CONTACT);
});
const enoughSpaceToSplit = computed<boolean>(() => width.value >= 700);
const splitPossible = computed<boolean>(() => props.canSplit && enoughSpaceToSplit.value);
const splitFlag = ref<boolean>(loadSplitFlag());
const isSplit = computed<boolean>(() => splitPossible.value && splitFlag.value);
const duoSplit = ref<InstanceType<typeof ContainerSplit>|null>(null);

const eventMove = computed<boolean>(() => {
	return hasValue(today.createContext, ContextKey.EVENT);
});

function toggleDualSplit() {
	splitFlag.value = !splitFlag.value;
	saveSplitFlag();
}

interface RefLinkParams {
	customer?: number;
	branch?: number;
	person?: number;

	srcType?: number;
	srcId?: number;
}

const refLinks = computed<RefLinkParams[]>(() => {
	const pars: RefLinkParams[] = [];
	const ccx = today.createContext;
	if (hasValue(ccx, ContextKey.CUSTOMER)) pars.push({ customer: getIntValue(ccx, 'customer'), branch: getIntValue(ccx, 'branch'), person: getIntValue(ccx, 'person') });
	if (hasValue(ccx, ContextKey.OPPORTUNITY)) pars.push({ srcType: SrcType.OPPORTUNITY, srcId: getIntValue(ccx, ContextKey.OPPORTUNITY) });
	if (hasValue(ccx, ContextKey.BUSCASE)) pars.push({ srcType: SrcType.BUSCASE, srcId: getIntValue(ccx, ContextKey.BUSCASE) });
	if (hasValue(ccx, ContextKey.PROJECT)) pars.push({ srcType: SrcType.TASK, srcId: getIntValue(ccx, ContextKey.PROJECT) });
	if (hasValue(ccx, ContextKey.PROBLEM)) pars.push({ srcType: SrcType.PROBLEM, srcId: getIntValue(ccx, ContextKey.PROBLEM) });
	if (hasValue(ccx, ContextKey.CAMPAIGN)) pars.push({ srcType: SrcType.CAMPAIGN, srcId: getIntValue(ccx, ContextKey.CAMPAIGN) });
	if (hasValue(ccx, ContextKey.CONTACT)) pars.push({ srcType: SrcType.CONTACT, srcId: getIntValue(ccx, ContextKey.CONTACT) });
	return pars;
});

function initialHeight() {
	let height = 0;
	if (props.name) {
		const savedHeight = localStorage.getItem(props.name + ".height");
		if (savedHeight) {
			height = parseInt(savedHeight);
		}
	}
	if (height < MIN_HEIGHT) {
		if (props.initialHeight) height = props.initialHeight;
	}
	if (height < MIN_HEIGHT) {
		height = Math.floor(document.documentElement.clientHeight * 0.75);
	}
	if (height < MIN_HEIGHT) height = MIN_HEIGHT;
	return height;
}

let startHeight: number = 0;
let startClientY: number = 0;

function resizeStart(e: PointerEvent) {
	e.preventDefault();
	if (resizer.value) {
		resizer.value.addEventListener("pointermove", resize);
		resizer.value.addEventListener("pointerup", resizeEnd);
		resizer.value.setPointerCapture(e.pointerId);
		startHeight = height.value;
		startClientY = e.clientY;
	}
}

function resize(e: PointerEvent) {
	e.preventDefault();
	if (resizer.value) {
		const diff = e.clientY - startClientY;
		height.value = Math.max(Math.floor(startHeight + diff), MIN_HEIGHT);
	}
}

function resizeEnd(e: PointerEvent) {
	if (resizer.value) {
		resizer.value.removeEventListener("pointermove", resize);
		resizer.value.removeEventListener("pointerup", resizeEnd);
		resizer.value.releasePointerCapture(e.pointerId);
		if (props.name && height.value !== startHeight) {
			localStorage.setItem(props.name + ".height", height.value.toString());
		}
	}
}

const wrapperStyle = computed<CSSProperties>(() => {
	const s: CSSProperties = {};
	s.height = (height.value + RESIZER_HEIGHT) + "px";
	return s;
});

function wrapperResized(entries: ResizeObserverEntry[]) {
	if (entries.length > 0) {
		width.value = Math.floor(entries[0].contentRect.width);
	}
}

const SPLIT_SAVE = "calendar-split-";

function saveSplitFlag() {
	if (props.name) {
		sessionStorage.setItem(SPLIT_SAVE + props.name, splitFlag.value.toString());
	}
}

function loadSplitFlag():boolean {
	if (props.name) {
		let value = sessionStorage.getItem(SPLIT_SAVE + props.name);
		return value && value === "true";
	}
	return false;
}

// Create a new ResizeObserver and pass the callback function
const resizeObserver = new ResizeObserver(wrapperResized);
const wrapper = ref<HTMLDivElement | null>(null);

onMounted(() => {
	if (wrapper.value) resizeObserver.observe(wrapper.value);
});
onUnmounted(() => {
	if (wrapper.value) resizeObserver.unobserve(wrapper.value);
});
</script>

<style>
.canvas-calendar-wrapper {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: flex-start;
	width: 100%;
	background-color: var(--bg);
}

.dark .canvas-calendar-wrapper {
}

.calendar-planner {
	width: calc(100% + var(--form-left-gutter) + var(--form-right-gutter));
	display: flex;
	flex-direction: row;
	column-gap: 16px;
	justify-content: space-between;
	align-items: baseline;

	background-color: var(--h16);
	border-top: 1px solid var(--h14);
	border-bottom: 1px solid var(--h14);
	padding-block: 8px;
	margin-bottom: 8px;
	padding-left: var(--form-left-gutter);
	padding-right: var(--form-right-gutter);
	margin-left: calc(-1 * var(--form-left-gutter));
	margin-right: calc(-1 * var(--form-right-gutter));
}

.dark .calendar-planner {
	background-color: var(--h1-2);
	border-top: 1px solid var(--h2-3);
	border-bottom: 1px solid var(--h2-3);
}

.line-items {
	display: flex;
	flex-direction: row;
	column-gap: 8px;
	flex-wrap: wrap;
	align-items: baseline;
}

.animated-click {
	position: relative;
	transform: translate(-4px, -4px);
	animation: click-drag 4s ease infinite;
}

.splitters {
	width: 100%;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	margin-top: 4px;
	height: var(--icn-size);
	column-gap: 8px;

	& > .horizontal-splitter {
		flex: 1 1;
	}

	& > .clone-icon {
		margin-right: 2px;
	}
}

@keyframes click-drag {
	0%, 65%, 100% {
		transform: translate(0, 0);
	}
	15% {
		transform: translate(-4px, -4px);
	}
	50% {
		transform: translate(6px, 6px);
	}
}
</style>